// src/components/History/SaleDetailsDialog.js
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/material/styles';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1.5),
  },
}));

const formatMoney = (v) => {
  const num = Number(v);
  if (!Number.isFinite(num)) return '0.00';
  return num.toFixed(2);
};

const SaleDetailsDialog = ({ open, onClose, sale }) => {
  if (!open || !sale) return null;

  const items = Array.isArray(sale.items) ? sale.items : [];

  const createdAt = sale.createdAt ? new Date(sale.createdAt) : null;
  const dateStr = createdAt ? createdAt.toLocaleDateString() : '—';
  const timeStr = createdAt
    ? createdAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '—';

  const fallbackSubTotal = items.reduce((sum, line) => {
    const qty = Number(line.qty ?? line.quantity ?? 1);
    const rawUnit = Number(
      line.unitPrice ?? line.price ?? line.RetailPrice ?? 0
    );
    const discount = Number(line.discount ?? 0);
    const discountApplied =
      typeof line.discountApplied === 'boolean'
        ? line.discountApplied
        : discount > 0;

    const unitAfter =
      Number(line.unitPriceAfterDiscount) ||
      (discountApplied ? rawUnit * (1 - discount / 100) : rawUnit);

    return sum + unitAfter * qty;
  }, 0);

  const subTotal = Number(sale.subTotal ?? 0) || fallbackSubTotal;

  const billDiscountPercent = Number(sale.billDiscountPercent ?? 0);
  const billDiscountAmount =
    Number(sale.billDiscountAmount ?? 0) ||
    (billDiscountPercent > 0 ? subTotal * (billDiscountPercent / 100) : 0);

  const total =
    Number(sale.total ?? 0) || Math.max(0, subTotal - billDiscountAmount);

  return (
    <BootstrapDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="sale-details-dialog-title"
    >
      <DialogTitle id="sale-details-dialog-title" sx={{ pr: 6 }}>
        Sale details
      </DialogTitle>

      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent dividers>
        {/* Top meta */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 1.5,
            mb: 2,
            fontSize: 14,
          }}
        >
          <Box>
            <Typography variant="body1" sx={{ color: '#333', fontWeight: 600 }}>
              Ref No
            </Typography>
            <Typography variant="body2" >
              {sale.refNo || sale.referenceNo || sale._id || '—'}
            </Typography>
          </Box>

          <Box>
            <Typography variant="body1" sx={{ color: '#333', fontWeight: 600 }}>
              Payment method
            </Typography>
            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
              {sale.paymentMethod || '—'}
            </Typography>
          </Box>

          <Box>
            <Typography variant="body1" sx={{ color: '#333', fontWeight: 600 }}>
              Date
            </Typography>
            <Typography variant="body2">{dateStr}</Typography>
          </Box>

          <Box>
            <Typography variant="body1" sx={{ color: '#333', fontWeight: 600 }}>
              Time
            </Typography>
            <Typography variant="body2">{timeStr}</Typography>
          </Box>

          <Box>
            <Typography variant="body1" sx={{ color: '#333', fontWeight: 600 }}>
              Number of items
            </Typography>
            <Typography variant="body2">{items.length}</Typography>
          </Box>
        </Box>

        {/* <Divider sx={{ my: 1.5 }} /> */}

        {/* Items table with batch */}
        <Typography variant="body1" sx={{ color: '#fff', background:'#333', paddingLeft:'15px', fontWeight: 600, marginTop:'20px' }}>
          Items
        </Typography>

        {items.length === 0 ? (
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            No items found for this sale.
          </Typography>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Batch</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">Discount (%)</TableCell>
                  <TableCell align="right">Line Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((line, idx) => {
                  const name =
                    line.medicineName ||
                    line.brandName ||
                    line.name ||
                    'Item';

                  const batchNo = line.batchNo || '—';
                  const qty = Number(line.qty ?? line.quantity ?? 1);

                  const rawUnit = Number(
                    line.unitPrice ?? line.price ?? line.RetailPrice ?? 0
                  );

                  const discount = Number(line.discount ?? 0);
                  const discountApplied =
                    typeof line.discountApplied === 'boolean'
                      ? line.discountApplied
                      : discount > 0;

                  const unitAfter =
                    Number(line.unitPriceAfterDiscount) ||
                    (discountApplied
                      ? rawUnit * (1 - discount / 100)
                      : rawUnit);

                  const lineTotal =
                    Number(line.lineTotal) || unitAfter * qty;

                  return (
                    <TableRow key={line._id || idx}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{batchNo}</Typography>
                      </TableCell>
                      <TableCell align="right">{qty}</TableCell>
                      <TableCell align="right">
                        {formatMoney(unitAfter)}
                      </TableCell>
                      <TableCell align="right">
                        {discountApplied && discount > 0
                          ? discount.toFixed(1)
                          : '0.0'}
                      </TableCell>
                      <TableCell align="right">
                        {formatMoney(lineTotal)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Totals */}
        {/* <Divider sx={{ my: 1.5 }} /> */}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: 0.5,
            mt: 1,
            fontSize: 14,
          }}
        >
          <Box sx={{ display: 'flex', gap: 4, marginTop:'50px' }}>
            <Typography variant="body2">Subtotal</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Rs {formatMoney(subTotal)}
            </Typography>
          </Box>

          {billDiscountAmount > 0 && (
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Typography variant="body2">Bill discount</Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                − Rs {formatMoney(billDiscountAmount)}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 4, mt: 1 }}>
            <Typography variant="subtitle2">Total</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Rs {formatMoney(total)}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </BootstrapDialog>
  );
};

export default SaleDetailsDialog;
